import { atlPageQuery, useVoidAtlPageMutation } from "@common/api/atl"
import type { AtlPageStatus } from "@common/client"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { handleError } from "@common/utils"
import { useQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"
import { CLOSED_SCAN } from "@/routes/_layout/aircraft_/$aircraftId/tech-log/-components/source"

export const Route = createFileRoute("/_layout/tech-logs/delete/$pageId")({
  component: RemovePageDialog,
})

type Removal = {
  title: string
  description: string
  action: string
  pending: string
  done: string
}

function removalOf(status: AtlPageStatus, label: string): Removal {
  switch (status) {
    case "draft":
      return {
        title: `Delete the draft ${label}?`,
        description:
          "Everything read from the scan is deleted. The scan itself stays in your documents.",
        action: "Delete",
        pending: "Deleting…",
        done: "The draft is gone",
      }
    case "recorded":
      return {
        title: `Delete ${label}?`,
        description:
          "The page leaves the tech log and its flying comes off the aircraft's hours and cycles. Deferrals it cleared reopen, and deferrals only it raised are withdrawn.",
        action: "Delete",
        pending: "Deleting…",
        done: "Removed from the tech log",
      }
  }
}

function RemovePageDialog(): React.ReactElement {
  const { pageId } = Route.useParams()
  const { data: page } = useQuery(atlPageQuery({ atl_page_id: pageId }))
  const mutation = useVoidAtlPageMutation()
  const navigate = useNavigate({ from: Route.fullPath })
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const showToast = useCustomToast()
  const removal = removalOf(
    page?.status ?? "draft",
    page?.log_number ?? "this page",
  )
  const toList = () =>
    navigate({
      to: "/tech-logs",
      search: (previous) => ({
        ...previous,
        ...CLOSED_SCAN,
        log_tab: undefined,
        wide: undefined,
      }),
    })
  const back = () => (canGoBack ? router.history.back() : toList())

  const confirm = () =>
    mutation.mutate(
      { atl_page_id: pageId },
      {
        onSuccess: () => {
          showToast(page?.log_number ?? "Page", removal.done, "success")
          toList()
        },
        onError: (error) => handleError(error, showToast),
      },
    )

  return (
    <ConfirmDialog
      title={removal.title}
      description={removal.description}
      action={removal.action}
      pendingAction={removal.pending}
      pending={mutation.isPending}
      disabled={!page}
      onConfirm={confirm}
      onCancel={back}
    />
  )
}
